import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  ComparacaoVersoesResponse, ListaOrcamentosResponse, ListaPrecosResponse, ListarOrcamentosQuery, ResumoOrcamentosQuery, ResumoOrcamentosResponse, OrcamentoDto,
  OrcamentoResponse, OrcamentoSimuladoResponse, RecalculoOrcamentoResponse, MudarSituacaoDto, VersaoResumoResponse, AtualizarParametrosResponse,
} from '@triade/contracts';
import { ApiError, requisitar } from '@/lib/api';
import { useSessao } from '@/lib/session';
import { useConsulta, useMutacao } from './base';

/**
 * POST /orcamentos/simular como consulta (nao grava). `dto` nulo = desligada. Mantem o resultado anterior enquanto
 * o novo carrega. Todo numero que a tela mostra vem daqui (ou do orcamento gravado).
 */
export function useSimulacaoOrcamento(dto: OrcamentoDto | null) {
  const { empresaId } = useSessao();
  return useQuery<OrcamentoSimuladoResponse, ApiError>({
    queryKey: ['orcamento-simulacao', empresaId, dto],
    queryFn: () => requisitar<OrcamentoSimuladoResponse>('/orcamentos/simular', { method: 'POST', body: dto }),
    enabled: dto !== null && empresaId !== null,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * Preco inicial de um item recem-adicionado: ultimo praticado da tabela/condicao, senao o sugerido arredondado,
 * ambos ja entregues pela API (GET /precificacao/lista). Item sem custo/tributo volta em `pulado`.
 */
export async function buscarPrecoInicial(p: {
  itemId: string; codigo: string; tabelaId: string; condicaoId: string; data: string;
  /** Politica NEGOCIADO: o preço inicial parte do sugerido (nao do praticado da lista). */
  politica?: 'NEGOCIADO' | 'TABELA';
}): Promise<{ preco: string } | { pulado: string }> {
  const r = await requisitar<ListaPrecosResponse>('/precificacao/lista', {
    query: { q: p.codigo, tabelaId: p.tabelaId, condicaoId: p.condicaoId, data: p.data, limit: 20 },
  });
  const linha = r.itens.find((l) => l.itemId === p.itemId);
  if (linha) return { preco: p.politica === 'NEGOCIADO' ? linha.sugeridoExibicao : (linha.praticado ?? linha.sugeridoExibicao) };
  const pulado = r.skipped.find((l) => l.itemId === p.itemId);
  if (pulado) return { pulado: `${pulado.message ?? 'Sem custo ou tributos cadastrados'} (${pulado.reason})` };
  const ignorada = r.tabelasIgnoradas.find((t) => t.tabelaId === p.tabelaId);
  return { pulado: ignorada ? `${ignorada.message} (${ignorada.code})` : 'Item sem preço calculável nesta tabela e condição.' };
}

/** Pagina + total real do filtro (GET /orcamentos). Filtros e ordenacao rodam no servidor. */
export function useOrcamentos(q: Partial<ListarOrcamentosQuery> = {}, opts: { ativo?: boolean; manter?: boolean } = {}) {
  const { empresaId } = useSessao();
  return useQuery<ListaOrcamentosResponse, ApiError>({
    queryKey: ['orcamentos', 'lista', empresaId, q],
    queryFn: () => requisitar<ListaOrcamentosResponse>('/orcamentos', { query: q }),
    enabled: (opts.ativo ?? true) && empresaId !== null,
    ...(opts.manter ? { placeholderData: keepPreviousData } : {}),
  });
}

/** Contagem e soma por status etc. (GET /orcamentos/resumo), agregados no servidor. */
export function useResumoOrcamentos(q: Partial<ResumoOrcamentosQuery> = {}) {
  const { empresaId } = useSessao();
  return useQuery<ResumoOrcamentosResponse, ApiError>({
    queryKey: ['orcamentos', 'resumo', empresaId, q],
    queryFn: () => requisitar<ResumoOrcamentosResponse>('/orcamentos/resumo', { query: q }),
    enabled: empresaId !== null,
    placeholderData: keepPreviousData,
  });
}

export function useOrcamento(id: string | undefined) {
  return useConsulta<OrcamentoResponse>(['orcamentos', id], `/orcamentos/${id}`, { ativo: !!id });
}

export function useCriarOrcamento() {
  return useMutacao<OrcamentoResponse, OrcamentoDto>({
    metodo: 'POST', caminho: () => '/orcamentos', corpo: (v) => v, invalidar: [['orcamentos']],
  });
}

/** PUT regrava o orcamento inteiro (so em RASCUNHO; fora disso a API responde 409). */
export function useAtualizarOrcamento() {
  return useMutacao<OrcamentoResponse, { id: string } & OrcamentoDto>({
    metodo: 'PUT',
    caminho: (v) => `/orcamentos/${v.id}`,
    corpo: ({ id: _id, ...resto }) => resto,
    invalidar: [['orcamentos']],
  });
}

/** POST /orcamentos/:id/situacao. PERDIDO exige `motivo` (a API responde 422 MOTIVO_OBRIGATORIO sem ele). */
export function useMudarSituacaoOrcamento() {
  return useMutacao<OrcamentoResponse, { id: string } & MudarSituacaoDto>({
    metodo: 'POST',
    caminho: (v) => `/orcamentos/${v.id}/situacao`,
    corpo: ({ id: _id, ...resto }) => resto,
    invalidar: [['orcamentos']],
  });
}

export function useRecalcularOrcamento() {
  return useMutacao<RecalculoOrcamentoResponse, { id: string }>({
    metodo: 'POST', caminho: (v) => `/orcamentos/${v.id}/recalcular`,
  });
}

/** Uma versao especifica (GET /orcamentos/:id/versoes/:n): mesma forma do detalhe, sem a aba Versoes. */
export function useVersaoOrcamento(id: string | undefined, n: number | undefined) {
  return useConsulta<OrcamentoResponse>(['orcamentos', id, 'versao', n], `/orcamentos/${id}/versoes/${n}`, { ativo: !!id && n !== undefined });
}

/** PUT /orcamentos/:id/versoes/:n: edita o rascunho vN (versao enviada: 409 IMUTAVEL). */
export function useAtualizarVersao() {
  return useMutacao<OrcamentoResponse, { id: string; n: number } & OrcamentoDto>({
    metodo: 'PUT',
    caminho: (v) => `/orcamentos/${v.id}/versoes/${v.n}`,
    corpo: ({ id: _id, n: _n, ...resto }) => resto,
    invalidar: [['orcamentos']],
  });
}

/** "Editar" numa versao enviada: cria a PROXIMA versao como rascunho (copia da de referencia). */
export function useCriarProximaVersao() {
  return useMutacao<OrcamentoResponse, { id: string }>({
    metodo: 'POST', caminho: (v) => `/orcamentos/${v.id}/versoes`, invalidar: [['orcamentos']],
  });
}

/** "Enviar ao cliente": congela a versao vN (snapshot, validade, PDF guardado com sha256). */
export function useEnviarVersao() {
  return useMutacao<OrcamentoResponse, { id: string; n: number }>({
    metodo: 'POST', caminho: (v) => `/orcamentos/${v.id}/versoes/${v.n}/enviar`, invalidar: [['orcamentos']],
  });
}

/** Aprova UMA versao enviada; as demais ficam como historico. */
export function useAprovarVersao() {
  return useMutacao<OrcamentoResponse, { id: string; n: number }>({
    metodo: 'POST', caminho: (v) => `/orcamentos/${v.id}/versoes/${v.n}/aprovar`, invalidar: [['orcamentos']],
  });
}

/** Descarta o rascunho vN (N >= 2; a API recusa a v1). */
export function useDescartarVersao() {
  return useMutacao<OrcamentoResponse, { id: string; n: number }>({
    metodo: 'POST', caminho: (v) => `/orcamentos/${v.id}/versoes/${v.n}/descartar`, invalidar: [['orcamentos']],
  });
}

/** Aba Versoes: todas as versoes (inclusive descartadas), com total, lucro, quem enviou e a validade (GET :id/versoes). */
export function useVersoesOrcamento(id: string | undefined) {
  return useConsulta<VersaoResumoResponse[]>(['orcamentos', id, 'versoes'], `/orcamentos/${id}/versoes`, { ativo: !!id });
}

/** Comparacao entre duas versoes (GET :id/versoes/comparar?a=&b=): tudo calculado na API. Nao roda com a === b. */
export function useComparacaoVersoes(id: string | undefined, a: number | undefined, b: number | undefined) {
  return useConsulta<ComparacaoVersoesResponse>(['orcamentos', id, 'comparar'], `/orcamentos/${id}/versoes/comparar`, {
    query: { a: a ?? 0, b: b ?? 0 },
    ativo: !!id && a !== undefined && b !== undefined && a !== b,
  });
}

/**
 * POST :id/versoes/com-custos-atuais (E12): cria a proxima versao RASCUNHO com os precos da versao de referencia e o custo
 * de hoje (a API recalcula tudo). Nao envia. 409 RASCUNHO_ABERTO / SEM_VERSAO_ENVIADA / TRANSICAO_INVALIDA / SOMENTE_LEITURA.
 */
export function useCriarVersaoComCustosAtuais() {
  return useMutacao<OrcamentoResponse, { id: string }>({
    metodo: 'POST', caminho: (v) => `/orcamentos/${v.id}/versoes/com-custos-atuais`, invalidar: [['orcamentos']],
  });
}

/** POST /orcamentos/simular com outra data de faturamento (nao grava): "este orcamento em 2027". Mesmo corpo, so `dataFaturamento` muda. */
export function useSimularOrcamentoEm() {
  return useMutacao<OrcamentoSimuladoResponse, OrcamentoDto>({ metodo: 'POST', caminho: () => '/orcamentos/simular', corpo: (v) => v });
}

/**
 * POST :id/versoes/:n/atualizar-parametros. `confirmar:false` so devolve o antes x depois (nada grava); `true` regrava o
 * rascunho com os parametros vigentes. So rascunho (409 VERSAO_SOMENTE_LEITURA nas enviadas).
 */
export function useAtualizarParametros() {
  return useMutacao<AtualizarParametrosResponse, { id: string; n: number; confirmar: boolean }>({
    metodo: 'POST',
    caminho: (v) => `/orcamentos/${v.id}/versoes/${v.n}/atualizar-parametros`,
    corpo: ({ confirmar }) => ({ confirmar }),
    invalidar: [['orcamentos']],
  });
}
